'use client';

import coinWhite from '@/assets/images/icons/coin-white.png';
import ActionButton from '@/components/form/ActionButton/ActionButton';
import IconTextUnit from '@/components/ui/IconTextUnit/IconTextUnit';
import ModalBase from '@/components/ui/ModalBase/ModalBase';
import { LOTTERY_STATUS, OAUTH_PROVIDER } from '@/constants';
import { Format } from '@/lib/dateFns';
import { swal } from '@/lib/sweetalert';
import { getCookieValue } from '@/utils/cookie';
import { diffDateTimeHours, newDate } from '@/utils/date';
import { formatCurrency, formatDateCommon } from '@/utils/formatters';
import { goMainOneTimeAuth } from '@/utils/functions';
import { isAfter, isBefore } from 'date-fns';
import type { Session } from 'next-auth';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Swal from 'sweetalert2';
import type { LotteryEventDetail } from '../../types';
import CarolLotteryDrawButton from '../CarolLotteryDrawButton/CarolLotteryDrawButton';
import LotteryDrawButton from '../LotteryDrawButton/LotteryDrawButton';
import styles from './LotteryFab.module.scss';

//import { pushToDataLayer } from '@/components/analytics/ga-core';
import utilsStyles from '@/styles/modules/utils.module.scss';
import type { Route } from 'next';

function LotteryFab({
  lotteryEvent,
  session = null,
  needsDAccountLink = false,
  daccountRegisterUrl,
}: {
  lotteryEvent: LotteryEventDetail;
  session?: Session | null;
  needsDAccountLink?: boolean;
  daccountRegisterUrl?: string | null;
}) {
  const [timeLeft, setTimeLeft] = useState('');
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenNotice, setIsOpenNotice] = useState(false);
  const [currentShopName, setCurrentShopName] = useState<string | undefined>(undefined);
  const [hasCarolCookie, setHasCarolCookie] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      if (shop) {
        setCurrentShopName(shop);
      }

      // carol連携くじのcookieをチェック
      const carolCookieName =
        session?.user.id !== undefined
          ? `carol_lottery_${lotteryEvent.id}_${session.user.id}`
          : `carol_lottery_${lotteryEvent.id}`;
      const cookieValue = getCookieValue(carolCookieName);
      setHasCarolCookie(cookieValue === '1');
    }
  }, [lotteryEvent.id, session?.user.id]);

  useEffect(() => {
    const endAtDate = newDate(lotteryEvent.endAt);
    const interval = window.setInterval(() => {
      const currentDate = newDate();
      const diff = diffDateTimeHours(endAtDate, currentDate);
      setTimeLeft(`${diff.days}日${diff.hours}時間${diff.minutes}分${diff.seconds}秒`);
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [lotteryEvent.endAt]);

  const handleDaccountRegisterClick = () => {
    if (daccountRegisterUrl) {
      window.location.href = daccountRegisterUrl;
    } else {
      swal.messages({ messages: ['dアカウント登録URLの取得に失敗しました'] });
    }
  };

  const goLotteryDPayOnly = () => {
    router.push(`/lotteries/${lotteryEvent.customOptions?.isPickujiUsual}`);
  };

  const goLotteryConfirmUsual = () => {
    router.push(`/lotteries/${lotteryEvent.id}/confirm`);
  };

  const AddressAlertContent = () => {
    const handleAddressRegister = () => {
      window.location.href = `/account/address?callback=${encodeURIComponent(location.href)}`;
    };

    const handleCancel = () => {
      Swal.close();
    };

    return (
      <>
        <div className={styles.addressAlertContent}>
          <p className={styles.addressAlertMessage}>くじを引く前に配送先情報の入力が必要です</p>
        </div>
        <div className={styles.addressAlertButtons}>
          <button
            type='button'
            className={`${styles.addressAlertButton} ${styles.addressAlertPrimaryButton}`}
            onClick={handleAddressRegister}
          >
            配送先登録画面へ
          </button>
          <button
            type='button'
            className={`${styles.addressAlertButton} ${styles.addressAlertCancelButton}`}
            onClick={handleCancel}
          >
            キャンセル
          </button>
        </div>
      </>
    );
  };

  const openAddressAlert = async () => {
    const { isConfirmed } = await swal.fire({
      title: '配送先情報が未入力です',
      html: '<div id="swal-content"></div>',
      showConfirmButton: false,
      showCancelButton: false,
      didOpen: () => {
        const container = document.getElementById('swal-content');
        if (container) {
          const root = createRoot(container);
          root.render(<AddressAlertContent />);
        }
      },
    });
    if (isConfirmed) {
      router.push(`/account/address?callback=${location.href}`);
    }
  };

  const goLotteryConfirm = () => {
    if (needsDAccountLink) {
      //ログインしているかチェック
      if (!session?.user) {
        router.push(`/auth/signin?callbackUrl=/lotteries/${lotteryEvent.id}`);
        return;
      }
      setIsOpen(true);
      return;
    }

    if (lotteryEvent.customOptions?.isPickujiUsual) {
      setIsOpenNotice(true);
      return;
    }
    // ★★★ shopパラメータを次のURLに引き継ぐ ★★★
    const baseUrl = `/lotteries/${lotteryEvent.id}/confirm`;
    if (currentShopName) {
      router.push(`${baseUrl}?shop=${currentShopName}` as Route);
    } else {
      router.push(baseUrl as Route);
    }
  };

  const generateDaccountRedirectUrl = (
    baseUrl: string,
    apiBaseUrl: string,
    serviceId: string,
    lotteryEventId: number,
  ): string => {
    const encodedCallbackUrl = encodeURIComponent(`${baseUrl}/lotteries/${lotteryEventId}`);
    const encodedCancelUrl = encodeURIComponent(`${baseUrl}/lotteries/${lotteryEventId}`);
    return `${apiBaseUrl}/oauth/v1/link/${OAUTH_PROVIDER.daccount.value}/redirect?oem_service_id=${serviceId}&callback_url=${encodedCallbackUrl}&cancel_url=${encodedCancelUrl}`;
  };

  const daccountRedirect = async () => {
    if (!session?.user) {
      router.push(`/auth/signin?callbackUrl=/lotteries/${lotteryEvent.id}`);
      return;
    }
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
    const serviceId = process.env.NEXT_PUBLIC_SERVICE_ID;
    const lotteryEventId = lotteryEvent.id;

    if (!baseUrl || !apiBaseUrl || !serviceId) {
      return;
    }

    const redirectUrl = generateDaccountRedirectUrl(baseUrl, apiBaseUrl, serviceId, lotteryEventId);
    window.location.href = redirectUrl;
  };

  return (
    <>
      <div className={styles.componentStyle}>
        <div className={styles.container}>
          <div>
            <div className={`${styles.itemCoin} ${utilsStyles.whiteColor}`}>
              <p className={styles.drawSubText}>
                1回
                <span className={`${utilsStyles.textBold} ${styles.drawFeeText}`}>
                  {formatCurrency(lotteryEvent.drawFee)}
                </span>
                円（税込）
              </p>
              <p className={utilsStyles.textXSmall}>
                {hasShippingFee(lotteryEvent.price.shippingFee, lotteryEvent.price.additionalShippingFee)
                  ? '（配送手数料を含まない）'
                  : '（配送手数料を含む）'}
              </p>
              {isPeriodLottery(lotteryEvent.startAt, lotteryEvent.endAt) && (
                <p className={styles.timeLeft}>終了まで{timeLeft}</p>
              )}
            </div>
          </div>
          {rightArea(
            session,
            lotteryEvent,
            goLotteryConfirm,
            needsDAccountLink,
            openAddressAlert,
            hasCarolCookie,
            router,
          )}
        </div>
      </div>

      {/* OAuthモーダル */}
      <ModalBase
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        bgColor={'transparent'}
      >
        <div className={styles.modal}>
          <div className={styles.centerContent}>
            <p className={styles.modalTitle}>{lotteryEvent.name}の利用には</p>
            <p className={styles.modalTitle}>dアカウントの連携が必要です</p>

            <div className={`${utilsStyles.mtSmall} ${styles.buttonWrapper}`}>
              <button type='button' className={styles.daccountConnectButton} onClick={daccountRedirect}>
                dアカウントと連携する
              </button>
              <button
                type='button'
                className={`${styles.daccountRegisterButton}`}
                onClick={handleDaccountRegisterClick}
              >
                ※dアカウントをお持ちでない方はこちら
              </button>
            </div>
            <div className={utilsStyles.mtSmall}>
              <ActionButton onClick={() => setIsOpen(false)} outlined size='small'>
                キャンセル
              </ActionButton>
            </div>
          </div>
        </div>
      </ModalBase>

      {/* お知らせモーダル */}
      <ModalBase
        isOpen={isOpenNotice}
        onClose={() => {
          setIsOpenNotice(false);
        }}
        bgColor={'transparent'}
      >
        <div className={styles.modal}>
          <div className={styles.centerContent}>
            <p className={styles.noticeModalTitle}>お知らせ</p>
            <p className={styles.noticeModalContent}>
              【d払い決済】をご利用の方は <br />
              特別なイベントがご利用いただけます
            </p>

            <div className={`${utilsStyles.mtSmall} ${styles.buttonRowWrapper}`}>
              <button type='button' className={styles.noticeUsualEventButton} onClick={goLotteryDPayOnly}>
                d払い決済を利用する
              </button>
            </div>
            <button type='button' className={styles.noticeRegisterButton} onClick={goLotteryConfirmUsual}>
              その他決済を利用する
            </button>
          </div>
        </div>
      </ModalBase>
    </>
  );
}

function isPeriodLottery(startAt: string, endAt: string) {
  return endAt && nowLotteryStatus(startAt, endAt) === LOTTERY_STATUS.is_session.value;
}

function nowLotteryStatus(startAt: string, endAt: string) {
  if (endAt && isAfter(newDate(), newDate(endAt))) {
    return LOTTERY_STATUS.later.value;
  }
  if (isBefore(newDate(), newDate(startAt))) {
    return LOTTERY_STATUS.upcoming.value;
  }
  return LOTTERY_STATUS.is_session.value;
}

function requireAddress(session: Session | null) {
  return session?.user && !session.user.setAddress;
}

function rightArea(
  session: Session | null,
  lotteryEvent: LotteryEventDetail,
  goLotteryConfirm: () => void,
  needsDAccountLink: boolean,
  openAddressAlert: () => void,
  hasCarolCookie: boolean,
  router: ReturnType<typeof useRouter>,
) {
  const handleCustomPopup = async () => {
    // ログインしているかチェック
    if (!session?.user) {
      router.push(`/auth/signin?callbackUrl=/lotteries/${lotteryEvent.id}`);
      return;
    }

    // cookieがある場合は通常の遷移
    if (hasCarolCookie) {
      goLotteryConfirm();
      return;
    }

    // isCarolLotteryUrlが存在する場合はそのURLに遷移
    if (lotteryEvent.customOptions?.isCarolLotteryUrl) {
      window.location.href = lotteryEvent.customOptions.isCarolLotteryUrl;
      return;
    }

    const title = lotteryEvent.customOptions?.popUpMessageTitleLotteryEventEntry;
    const text = lotteryEvent.customOptions?.popUpMessageTextLotteryEventEntry;

    if (!title && !text) {
      goLotteryConfirm();
      return;
    }

    const { isConfirmed } = await swal.confirm({
      title,
      text,
      confirmButtonText: 'OK',
    });

    if (!isConfirmed) {
      return;
    }

    goLotteryConfirm();
  };

  switch (nowLotteryStatus(lotteryEvent.startAt, lotteryEvent.endAt)) {
    case LOTTERY_STATUS.upcoming.value:
      // 開催予定
      return (
        <div className={styles.upcomingButton}>
          <LotteryDrawButton disabled>
            <div className={utilsStyles.centeredVertical}>
              <p className={utilsStyles.textXSmall}>
                {formatDateCommon(lotteryEvent.startAt, Format.dateTimeLotteryJa)}
              </p>
              <p className={utilsStyles.textSmall}>スタート</p>
            </div>
          </LotteryDrawButton>
        </div>
      );
    case LOTTERY_STATUS.later.value:
      return <div className={styles.endTextWrapper}>このくじは終了しています</div>;
    default:
      // 住所入力必須
      return requireAddress(session) ? (
        <div className={styles.requireAddressButton}>
          <LotteryDrawButton color={'primary'} onClick={() => openAddressAlert()}>
            <div className={utilsStyles.centeredVertical}>
              <p className={utilsStyles.textSmall}>くじを引く前に</p>
              <p className={utilsStyles.textXSmall}>配送先情報を入力してください</p>
            </div>
          </LotteryDrawButton>
        </div>
      ) : (
        <div>
          {lotteryEvent.customOptions?.isCarolLotteryUrl && !hasCarolCookie ? (
            <CarolLotteryDrawButton color={'primary'} onClick={handleCustomPopup}>
              キャンペーンに参加してくじを引く
            </CarolLotteryDrawButton>
          ) : (
            <LotteryDrawButton color={'primary'} onClick={handleCustomPopup}>
              くじイベントに参加する
            </LotteryDrawButton>
          )}
          {needsDAccountLink && (
            <p className={`${utilsStyles.textXSmall} ${styles.daccountNote}`}>
              ※このイベントはdアカウント連携が必要です
            </p>
          )}
        </div>
      );
  }
}

// 送料があるか
function hasShippingFee(shippingFee?: number, additionalShippingFee?: number) {
  if (shippingFee && shippingFee > 0) return true;
  if (additionalShippingFee && additionalShippingFee > 0) return true;
  return false;
}

export default LotteryFab;
